import { Injectable, computed, signal, inject, effect } from '@angular/core';
import { Enums, Hunt } from '@cyborg-monorepo/hunter/common';
import { HuntService } from '../../../modules/shared/services/hunt.service';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { of, switchMap, catchError, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class HuntStateService {
  private readonly route = inject(ActivatedRoute);
  private readonly huntService = inject(HuntService);

  private readonly _loading = signal<boolean>(true);
  readonly loading = computed(() => this._loading());
  

  readonly priorityEnum = Enums.PriorityEnum;
  readonly statusEnum = Enums.StatusEnum;
  readonly outcomeEnum = Enums.OutcomeEnum;

  // Writable signal to hold the final hunt value
  private readonly _hunt = signal<Hunt | null>(null);
  readonly hunt = computed(() => this._hunt());

  private readonly _errorMessage = signal<string | null>(null);
  readonly errorMessage = computed(() => this._errorMessage());

  readonly complete = computed(() => this.hunt()?.status === Enums.StatusEnum.DONE);
  readonly id = computed(() => this.hunt()?.id ?? '');

  readonly name = computed(() => this.hunt()?.name ?? '');
  readonly environments = computed(() => this.hunt()?.environments ?? []);
  readonly editing = computed(() => this.hunt()?.editing ?? '');
  readonly abstract = computed(() => this.hunt()?.abstract ?? '');
  readonly hypothesis = computed(() => this.hunt()?.hypothesis ?? '');
  readonly notes = computed(() => this.hunt()?.notes ?? '');
  readonly outcome = computed(() => this.hunt()?.outcome ?? Enums.OutcomeEnum.NO_FINDINGS);
  readonly outcomeDependent = computed(() => this.hunt()?.outcomeDependent ?? '');

  readonly created = computed(() => this.hunt()?.created ? new Date(this.hunt()?.created).toLocaleDateString(undefined, { dateStyle: 'long' }) : '');
  readonly lastModified = computed(() => this.hunt()?.lastModified ? new Date(this.hunt()?.lastModified).toLocaleDateString(undefined, { dateStyle: 'long' }) : '');

  readonly huntTemplateName = computed(() => this.hunt()?.huntTemplate?.name ?? null);
  readonly huntTemplateId = computed(() => this.hunt()?.huntTemplate?.id ?? null);

  readonly assigneeFullName = computed(() => {
    const assignee = this.hunt()?.assignee;
    return assignee ? assignee.firstName + ' ' + assignee.lastName : 'Unassigned';
  });
  readonly authorFullName = computed(() => {
    const author = this.hunt()?.assignee;
    return author ? author.firstName + ' ' + author.lastName : 'Deleted';
  });

  constructor() {
    // Auto-load hunt based on route param
    toSignal(
        this.route.params.pipe(
          switchMap(params => {
            const id = params['id'];
            if (!id) {
              this._loading.set(false);
              return of(null);
            }
            this._loading.set(true);
            return this.huntService.getHuntById(id).pipe(
              tap(hunt => {
                this._hunt.set(hunt);
                this._loading.set(false);
              }),
              catchError(err => {
                console.error('Failed to load hunt', err);
                this._errorMessage.set('Failed to load hunt details.');
                this._loading.set(false);
                return of(null);
              })
            );
          })
        ),
        { initialValue: null }
      );
    
}

  updateHunt(updated: Hunt): void {
    this._hunt.set(updated);
  }
}
